import { apiFetch } from "@/lib/api";
import {
  uploadDirectMrpRfqAttachment,
  type RfqAttachmentUploadProgress,
} from "@/lib/rfqAttachmentUpload";

export type MrpRfqStatus = "draft" | "sent" | "quoted" | "converted" | "cancelled";

export interface MrpRfqTarget {
  id: number;
  quantity: number;
  target_unit_price: number | null;
  sort_order: number;
}

export interface MrpRfqQuoteRow {
  id: number;
  quantity: number;
  quoted_unit_price: number | null;
  quoted_lead_time_weeks: number | null;
  matched_target_id: number | null;
  matches_target: boolean;
}

export interface MrpRfqLine {
  id: number;
  line_number: number;
  sku: string;
  description: string | null;
  internal_notes: string | null;
  external_notes: string | null;
  targets: MrpRfqTarget[];
  quotes: MrpRfqQuoteRow[];
  selected_quote_id: number | null;
}

export type MrpRfqAttachmentAudience = "external" | "internal";

export interface MrpRfqAttachment {
  id: number;
  filename: string;
  content_type: string;
  file_size_bytes: number;
  uploaded_at: string;
  uploaded_by_email: string | null;
  audience: MrpRfqAttachmentAudience;
  request_for_quote_item_id: number | null;
  document_record_id: string | null;
  line_number: number | null;
  line_sku: string | null;
}

export interface MrpRfq {
  id: number;
  rfq_number: string;
  status: MrpRfqStatus;
  supplier_id: number;
  supplier_name: string | null;
  assigned_to_user_id: string | null;
  assigned_to_display_name: string | null;
  supplier_currency: string | null;
  location_id: number | null;
  location_code: string | null;
  internal_notes: string | null;
  external_notes: string | null;
  needed_by_date: string | null;
  currency: string;
  supplier_quote_reference: string | null;
  quote_valid_until: string | null;
  quoted_at: string | null;
  converted_public_purchase_order_id: number | null;
  converted_po_number: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  cancelled_at: string | null;
  freshdesk_ticket_ids: number[];
  lines: MrpRfqLine[];
  attachments: MrpRfqAttachment[];
}

export interface MrpRfqListRow {
  id: number;
  rfq_number: string;
  status: MrpRfqStatus;
  supplier_id: number;
  supplier_name: string | null;
  assigned_to_user_id: string | null;
  assigned_to_display_name: string | null;
  location_id: number | null;
  location_code: string | null;
  line_count: number;
  total_target_quantity: number;
  needed_by_date: string | null;
  created_at: string;
  sent_at: string | null;
  quoted_at: string | null;
  converted_public_purchase_order_id: number | null;
}

export interface MrpRfqSkuRelatedRow {
  rfq_id: number;
  rfq_number: string;
  rfq_item_id: number;
  status: MrpRfqStatus;
  supplier_name: string | null;
  currency: string;
  quote_id: number | null;
  quoted_quantity: number;
  quoted_unit_price: number | null;
  quoted_lead_time_weeks: number | null;
  quote_valid_until: string | null;
  quoted_at: string | null;
}

export interface MrpRfqTargetInput {
  /** Digits-only string while editing; number when loaded from the API. */
  quantity: number | string;
  target_unit_price?: number | null;
}

export interface MrpRfqLineInput {
  sku: string;
  description?: string | null;
  internal_notes?: string | null;
  external_notes?: string | null;
  targets: MrpRfqTargetInput[];
}

export interface MrpRfqQuoteRowInput {
  quantity: number;
  quoted_unit_price: number | null;
  quoted_lead_time_weeks?: number | null;
}

export interface MrpRfqQuoteLineInput {
  line_number: number;
  quotes: MrpRfqQuoteRowInput[];
}

export interface MrpRfqQuoteSelectionInput {
  line_number: number;
  quote_id: number;
}

export const MRP_RFQ_STATUS_LABEL: Record<MrpRfqStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  quoted: "Quoted",
  converted: "Converted to PO",
  cancelled: "Cancelled",
};

export const MRP_RFQ_STATUS_HOVER_HINT =
  "Each line can request quotes at multiple target quantities. After sending, record supplier responses at any quantity—matching targets are flagged automatically. Pick one quote per line before creating a purchase order.";

export function mrpRfqStatusBadgeTone(
  status: MrpRfqStatus,
): "neutral" | "success" | "warning" | "danger" | "info" | "muted" {
  if (status === "draft") return "info";
  if (status === "sent") return "warning";
  if (status === "quoted") return "success";
  if (status === "converted") return "muted";
  if (status === "cancelled") return "danger";
  return "neutral";
}

export async function fetchMrpRfqs(query?: {
  status?: string;
  location?: string;
  q?: string;
}): Promise<MrpRfqListRow[]> {
  const response = await apiFetch<{ rows: MrpRfqListRow[] }>("/api/mrp/rfqs", { query });
  return response.rows;
}

export async function fetchMrpRfqsBySku(sku: string): Promise<MrpRfqSkuRelatedRow[]> {
  const response = await apiFetch<{ rows: MrpRfqSkuRelatedRow[] }>("/api/mrp/rfqs/by-sku", {
    query: { sku },
  });
  return response.rows;
}

export async function fetchMrpRfq(id: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}`);
  return response.rfq;
}

export async function createMrpRfq(body: {
  supplier_id: number;
  location_code?: string | null;
  internal_notes?: string | null;
  external_notes?: string | null;
  needed_by_date?: string | null;
  currency?: string | null;
  lines: MrpRfqLineInput[];
}): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>("/api/mrp/rfqs", {
    method: "POST",
    body,
  });
  return response.rfq;
}

export async function patchMrpRfqHeader(
  id: number,
  body: {
    internal_notes?: string | null;
    external_notes?: string | null;
    needed_by_date?: string | null;
    currency?: string | null;
    assigned_to_user_id?: string | null;
  },
): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}`, {
    method: "PATCH",
    body,
  });
  return response.rfq;
}

export function mergeMrpRfqAssigneePatch(
  previous: MrpRfq,
  patch: Pick<MrpRfq, "assigned_to_user_id" | "assigned_to_display_name" | "updated_at">,
): MrpRfq {
  return {
    ...previous,
    assigned_to_user_id: patch.assigned_to_user_id,
    assigned_to_display_name: patch.assigned_to_display_name,
    updated_at: patch.updated_at || previous.updated_at,
  };
}

export function syncMrpRfqAssigneeListCaches(
  queryClient: {
    setQueriesData: <T>(
      filters: {
        predicate: (query: { queryKey: readonly unknown[] }) => boolean;
      },
      updater: (current: T | undefined) => T | undefined,
    ) => void;
  },
  rfqId: number,
  patch: Pick<MrpRfqListRow, "assigned_to_user_id" | "assigned_to_display_name">,
): void {
  queryClient.setQueriesData<MrpRfqListRow[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          key[0] === "mrp"
          && key[1] === "rfqs"
          && key.length >= 4
          && typeof key[2] === "string"
        );
      },
    },
    (current) => {
      if (!Array.isArray(current)) return current;
      return current.map((row) => (row.id === rfqId ? { ...row, ...patch } : row));
    },
  );
}

export function resolveMrpRfqAssigneeDisplayName(
  assignedToUserId: string | null,
  users: MrpRfqAssignee[],
): string | null {
  if (assignedToUserId == null || assignedToUserId.trim() === "") {
    return null;
  }
  const match = users.find((user) => user.id === assignedToUserId);
  if (match == null) {
    return null;
  }
  return match.display_name?.trim() || match.id;
}

export interface MrpRfqAssignee {
  id: string;
  display_name: string;
}

export const MRP_RFQ_ASSIGNEES_QUERY_KEY = ["mrp", "rfqs", "assignees"] as const;

export async function fetchMrpRfqAssignees(): Promise<{ users: MrpRfqAssignee[] }> {
  return apiFetch("/api/mrp/rfqs/assignees");
}

export async function takeOverMrpRfq(id: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/take-over`, {
    method: "POST",
  });
  return response.rfq;
}

export interface MrpRfqEmailPreview {
  rfqId: number;
  rfqNumber: string;
  supplierName: string | null;
  supplierFreshdeskCompanyId: number | null;
  supplierFreshdeskCompanyName: string | null;
  subject: string;
  htmlBody: string;
  textBody: string;
  recipients: Array<{
    email: string;
    name: string | null;
    source: "supplier_mirror" | "freshsales_contact" | "supplier_quote_contact";
  }>;
  fromMailbox: "vendors@smartgridsolutions.com";
  externalAttachments: Array<{
    filename: string;
    line_number: number | null;
    line_sku: string | null;
  }>;
}

export async function fetchMrpRfqEmailPreview(id: number): Promise<MrpRfqEmailPreview> {
  const response = await apiFetch<{ preview: MrpRfqEmailPreview }>(`/api/mrp/rfqs/${id}/email-preview`);
  return response.preview;
}

export async function sendMrpRfqEmail(
  id: number,
  body: { recipientEmail: string; ccEmails?: string[] },
): Promise<{ ok: boolean; freshdesk_id: number | null; rfq: MrpRfq }> {
  return apiFetch(`/api/mrp/rfqs/${id}/email`, {
    method: "POST",
    body,
  });
}

export async function replaceMrpRfqLines(id: number, lines: MrpRfqLineInput[]): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/lines`, {
    method: "PUT",
    body: { lines },
  });
  return response.rfq;
}

export async function sendMrpRfq(id: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/send`, { method: "POST" });
  return response.rfq;
}

export async function saveMrpRfqQuote(
  id: number,
  body: {
    supplier_quote_reference?: string | null;
    quote_valid_until?: string | null;
    lines: MrpRfqQuoteLineInput[];
  },
): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/quote`, {
    method: "PATCH",
    body,
  });
  return response.rfq;
}

export async function saveMrpRfqQuoteSelections(
  id: number,
  selections: MrpRfqQuoteSelectionInput[],
): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/quote-selections`, {
    method: "PATCH",
    body: { selections },
  });
  return response.rfq;
}

export async function cancelMrpRfq(id: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${id}/cancel`, { method: "POST" });
  return response.rfq;
}

export async function createPurchaseOrderComposeFromMrpRfq(
  id: number,
  selections?: MrpRfqQuoteSelectionInput[],
): Promise<{
  compose_href: string;
  location_code: string;
  supplier_id: number;
  rfq_id: number;
}> {
  return apiFetch(`/api/mrp/rfqs/${id}/create-purchase-order-compose`, {
    method: "POST",
    body: selections != null && selections.length > 0 ? { selections } : {},
  });
}

export async function uploadMrpRfqAttachment(
  id: number,
  file: File,
  options?: {
    audience?: MrpRfqAttachmentAudience;
    lineId?: number | null;
    onProgress?: (progress: RfqAttachmentUploadProgress) => void;
  },
): Promise<MrpRfq> {
  return uploadDirectMrpRfqAttachment(id, file, {
    audience: options?.audience ?? "internal",
    lineId: options?.lineId ?? null,
    onProgress: options?.onProgress ?? (() => undefined),
  });
}

export async function downloadMrpRfqAttachment(
  rfqId: number,
  attachmentId: number,
): Promise<{ url: string; filename: string }> {
  return apiFetch(`/api/mrp/rfqs/${rfqId}/attachments/${attachmentId}/download`);
}

export async function deleteMrpRfqAttachment(rfqId: number, attachmentId: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(
    `/api/mrp/rfqs/${rfqId}/attachments/${attachmentId}`,
    { method: "DELETE" },
  );
  return response.rfq;
}

export async function attachMrpRfqDocumentsFromLibrary(
  rfqId: number,
  input: {
    documentRecordIds: string[];
    audience: MrpRfqAttachmentAudience;
    lineId?: number | null;
  },
): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq; attachedCount: number }>(
    `/api/mrp/rfqs/${rfqId}/attachments/from-documents`,
    {
      method: "POST",
      body: {
        documentRecordIds: input.documentRecordIds,
        audience: input.audience,
        request_for_quote_item_id: input.lineId ?? null,
      },
    },
  );
  return response.rfq;
}

export async function attachMrpRfqDocumentFromLibrary(
  rfqId: number,
  input: {
    documentRecordId: string;
    audience: MrpRfqAttachmentAudience;
    lineId?: number | null;
  },
): Promise<MrpRfq> {
  return attachMrpRfqDocumentsFromLibrary(rfqId, {
    documentRecordIds: [input.documentRecordId],
    audience: input.audience,
    lineId: input.lineId,
  });
}

export async function addMrpRfqFreshdeskTicket(rfqId: number, ticketId: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(`/api/mrp/rfqs/${rfqId}/freshdesk-tickets`, {
    method: "POST",
    body: { ticketId },
  });
  return response.rfq;
}

export async function removeMrpRfqFreshdeskTicket(rfqId: number, ticketId: number): Promise<MrpRfq> {
  const response = await apiFetch<{ rfq: MrpRfq }>(
    `/api/mrp/rfqs/${rfqId}/freshdesk-tickets/${ticketId}`,
    { method: "DELETE" },
  );
  return response.rfq;
}

export function emptyRfqTargetInput(): MrpRfqTargetInput {
  return { quantity: "1", target_unit_price: null };
}

export function parseRfqTargetQuantityInput(raw: string): string {
  return raw.replace(/,/g, "").replace(/[^\d]/g, "");
}

export function rfqTargetQuantityAsNumber(value: number | string): number {
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value).replace(/,/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function emptyRfqLineInput(): MrpRfqLineInput {
  return {
    sku: "",
    description: "",
    internal_notes: "",
    external_notes: "",
    targets: [emptyRfqTargetInput()],
  };
}

export function emptyQuoteRowInput(): MrpRfqQuoteRowInput {
  return { quantity: 0, quoted_unit_price: null, quoted_lead_time_weeks: null };
}
